import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchBySlug } from '../lib/cms';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { 
  GraduationCap, 
  ArrowRight, 
  CheckCircle2, 
  Phone, 
  Mail, 
  Sparkles, 
  Heart, 
  Users, 
  Compass, 
  ShieldCheck, 
  Briefcase, 
  Activity,
  MessageSquare,
  ClipboardCheck,
  UserCheck,
  Target,
  FileText,
  Download,
  X,
  Maximize2
} from 'lucide-react';

export default function STU() {
  useDocumentMeta(
    'STU - Særligt Tilrettelagt Ungdomsuddannelse | LIVSLYST ApS',
    'Læs om vores STU-tilbud hos LIVSLYST ApS. Vi tilbyder en 3-årig særligt tilrettelagt ungdomsuddannelse med fokus på personlig, social og faglig udvikling.'
  );

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('da', 'stu');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-stone-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const title = pageData?.title?.da || pageData?.title || 'STU';
  const intro = pageData?.intro?.da || pageData?.intro || '';
  const content = pageData?.content?.da || pageData?.content || '';
  const image = pageData?.image?.[0] || 'https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/8287d27c-1994-4cdd-8ea2-c18f78fd6eb3.PNG';

  const workAreas = [
    {
      title: 'Personlige & sociale kompetencer',
      description: 'Udvikling af den unges personlige identitet, selvforståelse og evne til at indgå i sociale fællesskaber.',
      icon: Users,
    },
    {
      title: 'Kommunikation & relationer',
      description: 'Støtte til at udtrykke egne behov og ønsker samt opbygge og vedligeholde betydningsfulde relationer.',
      icon: MessageSquare,
    },
    {
      title: 'Hverdagsmestring & livsduelighed',
      description: 'Praktisk træning i hverdagens gøremål, så den unge rüstes til et så selvstændigt voksenliv som muligt.',
      icon: Heart,
    },
    {
      title: 'Selvstændighed & ansvar',
      description: 'Individuel støtte til at tage ansvar for eget liv og træffe selvstændige valg baseret på egne ønsker.',
      icon: UserCheck,
    },
    {
      title: 'Praktiske færdigheder',
      description: 'Deltagelse i meningsfulde arbejdsfællesskaber og praktiske opgaver, der styrker arbejdsidentiteten.',
      icon: Briefcase,
    },
    {
      title: 'Sundhed & aktiv deltagelse',
      description: 'Fokus på fysisk og mental trivsel samt aktiv deltagelse i samfundslivet og fritidsaktiviteter.',
      icon: Activity,
    },
  ];

  const strengths = [
    'Selvværd og selvtillid',
    'Sociale kompetencer og relationsdannelse',
    'Kommunikative færdigheder',
    'Ansvarsfølelse og selvstændighed',
    'Livsduelighed og hverdagsmestring',
    'Deltagelseskompetencer i fællesskaber',
    'Forståelse af egne ressourcer og muligheder',
    'Mestringsstrategier',
    'Selvregulering',
  ];

  const corePurposes = [
    'Livsduelighed',
    'Fællesskab',
    'Udvikling',
    'Mestring',
    'Selvstændighed',
    'Dannelse',
    'Deltagelsesmuligheder',
    'Samarbejde',
    'Kommunikation',
    'Kompetenceudvikling',
    'Arbejdsidentitet og selvudvikling',
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      {/* Header Section */}
      <section className="bg-stone-100 py-16 md:py-20 border-b border-stone-200">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold flex items-center justify-center gap-1.5">
            <GraduationCap className="w-4.5 h-4.5" />
            <span>Ungdomsuddannelse</span>
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
            {title}
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
            {intro}
          </p>
        </motion.div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Text Column */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-6 space-y-6"
            >
              <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
                Hvad er STU hos LIVSLYST ApS?
              </h2>
              <div 
                className="text-stone-600 leading-relaxed space-y-4 text-base"
                dangerouslySetInnerHTML={{ __html: content }}
              />

            </motion.div>

            {/* Image Column - Now a beautiful illustrative image from CMS */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-6"
            >
              <div className="relative group block w-full rounded-2xl">
                <div className="absolute inset-0 bg-emerald-800 rounded-2xl rotate-2 scale-95 opacity-5 group-hover:rotate-3 transition-transform" />
                <div className="relative z-10 overflow-hidden rounded-2xl shadow-lg border border-stone-200">
                  <img 
                    src={image} 
                    alt="LIVSLYST STU" 
                    className="w-full h-auto max-h-[500px] object-cover bg-stone-50 group-hover:scale-[1.02] transition-transform duration-300"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dedicated Brochure Section */}
      <section className="py-16 md:py-24 bg-stone-100/50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Interactive Brochure Mockup */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 flex justify-center"
            >
              <button 
                onClick={() => setIsLightboxOpen(true)}
                className="relative group block w-full max-w-md text-left focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 rounded-2xl cursor-pointer"
              >
                <div className="absolute inset-0 bg-emerald-800 rounded-2xl rotate-3 scale-95 opacity-5 group-hover:rotate-4 transition-transform" />
                <div className="relative z-10 overflow-hidden rounded-2xl shadow-xl border border-stone-200 bg-white p-4">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=90,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/brochure-1.png" 
                    alt="LIVSLYST STU Brochure Forside" 
                    className="w-full h-auto object-contain bg-stone-50 rounded-lg group-hover:scale-[1.02] transition-transform duration-300"
                  />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white rounded-2xl">
                    <div className="bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg flex items-center gap-2 text-sm transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                      <Maximize2 className="w-4 h-4" />
                      <span>Læs i fuld skærm</span>
                    </div>
                  </div>
                </div>
              </button>
            </motion.div>

            {/* Right Column: Info & Download Actions */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Vores STU-brochure
              </h2>
              <p className="text-stone-600 text-base md:text-lg leading-relaxed">
                I vores brochure finder du en samlet præsentation af STU-tilbuddet hos LIVSLYST ApS. Brochuren er udarbejdet til ungestøtter, sagsbehandlere, forældre og de unge selv, der ønsker et hurtigt og overskueligt indblik i vores rammer og pædagogiske hverdag.
              </p>
              
              <div className="space-y-3 bg-white p-6 rounded-2xl border border-stone-200">
                <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider">Brochuren indeholder information om:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-stone-600 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                    Uddannelsens opbygning og mål
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                    De praktiske og kreative værksteder
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                    Vores pædagogiske metoder og værdier
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                    Praktikforløb og visitation
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  onClick={() => setIsLightboxOpen(true)}
                  className="flex items-center justify-center gap-2 px-6 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-semibold shadow-md transition-colors cursor-pointer text-center"
                >
                  <Maximize2 className="w-4 h-4" />
                  Læs online i fuld skærm
                </button>
                <button
                  onClick={() => handleDownload('https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/brochure-1.png', 'LIVSLYST-STU-Brochure.png')}
                  className="flex items-center justify-center gap-2 px-6 py-3.5 bg-white hover:bg-stone-50 text-stone-800 rounded-xl font-semibold border border-stone-300 shadow-xs transition-colors cursor-pointer text-center"
                >
                  <Download className="w-4 h-4 text-emerald-700" />
                  Download brochure (PNG)
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm">
          <div className="absolute inset-0 cursor-zoom-out" onClick={() => setIsLightboxOpen(false)} />
          <div className="relative z-10 max-w-5xl w-full max-h-[90vh] flex flex-col items-center">
            <button 
              onClick={() => setIsLightboxOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-stone-300 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
              aria-label="Luk"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="overflow-auto max-h-[80vh] rounded-xl shadow-2xl border border-white/10">
              <img 
                src="https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/brochure-1.png" 
                alt="LIVSLYST STU Brochure fuld størrelse" 
                className="max-w-full h-auto object-contain"
              />
            </div>
            <div className="mt-4 flex gap-4">
              <a 
                href="https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/brochure-1.png" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Åbn i nyt vindue
              </a>
              <button 
                onClick={() => setIsLightboxOpen(false)}
                className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Luk visning
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vores faglige ståsted */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Faglighed & Trivsel
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Vores faglige ståsted
            </h2>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed">
              Der arbejdes med at skabe struktur, genkendelighed og meningsfulde fællesskaber, som understøtter den unges trivsel, læring og deltagelse. Der er samtidig fokus på, at den unge mødes med anerkendelse, respekt og realistiske forventninger, så vedkommende oplever mestring og udvikler størst mulig selvstændighed.
            </p>
            <p className="text-stone-500 text-sm md:text-base leading-relaxed italic">
              Udvikling sker gennem deltagelse i hverdagsliv, undervisning, praktiske aktiviteter, sociale fællesskaber og jeg-støttende-samtaler, hvor den unge støttes i at udforske egne ressourcer, interesser og muligheder for fremtiden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {workAreas.map((area, idx) => {
              const IconComp = area.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-6 group-hover:bg-emerald-700 group-hover:text-white transition-colors">
                    <IconComp className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-3">
                    {area.title}
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {area.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Særlige behov & Hvad der styrkes */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column - Text */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5 space-y-6"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
                Individuel tilpasning
              </span>
              <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
                Støtte til særlige behov
              </h2>
              <p className="text-stone-600 text-base leading-relaxed">
                For unge med særlige behov, herunder Prader-Willi Syndrom, kan der være behov for ekstra tydelige rammer, høj grad af forudsigelighed og individuelt tilpasset støtte.
              </p>
              <p className="text-stone-600 text-base leading-relaxed">
                Gennem en struktureret og anerkendende pædagogisk tilgang understøttes den enkelte unge i at udvikle strategier, kompetencer og handlemuligheder, der styrker oplevelsen af mestring og deltagelse.
              </p>
              <div className="p-5 bg-stone-50 rounded-xl border border-stone-150 text-sm text-stone-600 leading-relaxed">
                Der arbejdes målrettet med at skabe deltagelsesmuligheder, hvor den unge oplever sig som en værdifuld del af et fællesskab. Samtidig understøttes personlig, social og faglig udvikling gennem tydelige rammer, anerkendende relationer, Low arousal og individuelle udviklingsmål.
              </div>
            </motion.div>

            {/* Right Column - Strengths list */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 bg-stone-50 p-8 md:p-10 rounded-2xl border border-stone-200"
            >
              <h3 className="text-xl font-bold text-stone-900 mb-6">
                Gennem undervisning, praktiske aktiviteter og fællesskaber styrkes den unges:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {strengths.map((strength, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-stone-700 text-sm md:text-base leading-snug">{strength}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold block mb-4">
            Mål & Retning
          </span>
          <h2 className="text-3xl font-bold text-stone-900 tracking-tight mb-6">
            Livslyst STU's overordnede formål
          </h2>
          <p className="text-stone-600 text-base max-w-2xl mx-auto mb-10">
            Uddannelsens overordnede formål er at understøtte den enkelte unge i en helhedsorienteret udvikling, der ruster dem til fremtiden:
          </p>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {corePurposes.map((purpose, idx) => (
              <span 
                key={idx}
                className="px-5 py-3 bg-white text-stone-800 text-sm md:text-base font-medium rounded-xl border border-stone-200 shadow-sm hover:border-emerald-500 hover:text-emerald-800 transition-colors cursor-default"
              >
                {purpose}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Match & Praktikforløb */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-emerald-50/50 rounded-3xl border border-emerald-100 p-8 md:p-12 space-y-8">
            <div className="flex items-center gap-3 text-emerald-800">
              <ClipboardCheck className="w-8 h-8 shrink-0" />
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                Hvordan sikrer vi det rette match?
              </h2>
            </div>
            
            <div className="space-y-6 text-stone-700 text-base md:text-lg leading-relaxed">
              <p>
                For at sikre det rette match mellem den unge og Livslyst STU indledes samarbejdet med en <strong>dialog mellem den unge, myndighed og Livslyst STU</strong>.
              </p>
              <p>
                Herefter tilbydes som udgangspunkt et <strong>praktikforløb</strong>, hvor både den unge og Livslyst STU får mulighed for at afprøve samarbejdet i praksis og vurdere, om tilbuddet skaber trivsel, mening og de rette udviklingsmuligheder.
              </p>
            </div>

            <div className="pt-6 border-t border-emerald-100/80 flex flex-col md:flex-row gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-emerald-700 text-white flex items-center justify-center shrink-0 mt-1">
                <Target className="w-5 h-5" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-stone-900 text-lg">Vores fælles mål</h4>
                <p className="text-stone-600 text-sm md:text-base leading-relaxed">
                  Målet er, at den unge opnår de bedst mulige forudsætninger for et aktivt og meningsfuldt voksenliv med størst mulig selvstændighed, deltagelse og livskvalitet – med udgangspunkt i egne ressourcer, interesser og individuelle potentialer der hvor de står i livet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="bg-stone-900 text-white rounded-2xl p-8 md:p-12 shadow-xl relative overflow-hidden"
          >
            {/* Decorative background blur */}
            <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-emerald-800/30 blur-3xl" />
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-4 text-center lg:text-left">
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                  Vil du høre mere om vores STU-tilbud?
                </h3>
                <p className="text-stone-300 text-sm md:text-base leading-relaxed">
                  Vi står altid klar til en uforpligtende snak om STU-forløb, optagelse eller en rundvisning i vores trygge rammer i Brørup. Kontakt os i dag, og lad os tage en snak om mulighederne.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-center items-center gap-4">
                <Link
                  to="/kontakt"
                  className="w-full text-center px-6 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-md transition-colors"
                >
                  Kontakt os nu
                </Link>
                <div className="flex flex-col items-center lg:items-start text-xs text-stone-400 space-y-1">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Phone className="w-3.5 h-3.5 text-emerald-500" />
                    Tlf: <a href="tel:51504654" className="hover:text-white transition-colors">51 50 46 54</a>
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Mail className="w-3.5 h-3.5 text-emerald-500" />
                    Email: <a href="mailto:Kontakt@livslystaps.dk" className="hover:text-white transition-colors">Kontakt@livslystaps.dk</a>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
